import Vue from 'vue';
import SessionTimeoutModal from '@/components/SessionTimeoutModal.vue';
import { LOGOUT_USER, FETCH_USER_DETAILS, TRANSLATIONS_FETCH } from '@/store/ActionTypes';
import store from "@/store/store";

// Warning after 15 minutes of inactivity, automatic logout after 20 minutes
const WARNING_MS = 15 * 60 * 1000; // 15 minutes
const LOGOUT_MS = 20 * 60 * 1000; // 20 minutes

declare global {
  interface Window {
    __sessionTimeoutDispose__?: (() => void) | null;
  }
}

export default function initSessionTimeout() {
  if (window.__sessionTimeoutDispose__) {
    window.__sessionTimeoutDispose__();
    window.__sessionTimeoutDispose__ = null;
  }

  // Helper function to get translations from store
  function getTranslations(): any {
    return store.state?.LocalizationModule?.translations ?? null;
  }

  // Fetch session timeout translations if not already loaded
  const fetchTranslations = async (): Promise<void> => {
    try {
      await store.dispatch(TRANSLATIONS_FETCH as any, {
        category: "pages",
        collections: ["common"],
      });
    } catch (e) {
      console.warn('Failed to fetch translations for session timeout', e);
    }
  };

  // Create a small Vue instance to host the modal so we don't need to change App.vue
  const modalVm = new Vue({
    data: {
      show: false,
      countdown: 0,
      translations: null as any,
    },
    render(h) {
      // cast `this` to any to avoid TypeScript complaining about method names
      const self: any = this;
      return h(SessionTimeoutModal as any, {
        props: {
          show: self.show,
          countdown: self.countdown,
          translations: self.translations,
        },
        on: {
          stay: self.onStay,
          signout: self.onSignOut,
        },
      });
    },
    methods: {
      async onStay() {
        try {
          // Ask the FETCH_USER_DETAILS action to suppress the global 401
          // redirect so we can handle failure locally instead of having the
          // repository immediately force a logout.
          await store.dispatch(FETCH_USER_DETAILS as any, {
            config: { headers: { 'X-Suppress-Auth-Redirect': '1' } },
          });
          // Session refresh successful, close modal and reset timers
          this.show = false;
          resetTimers();
        } catch (e) {
          // Session refresh failed, log out
          clearTimers();
          forceLogout();
        }
      },
      async onSignOut() {
        clearTimers();
        forceLogout();
      },
    },
  }).$mount();

  document.body.appendChild(modalVm.$el);
  // Expose modal VM for tests and debugging (removed on dispose)
  // NOTE: this is safe and cleaned up by dispose()
  // @ts-ignore
  window.__sessionTimeoutVm__ = modalVm;

  // Initialize translations
  fetchTranslations().then(() => {
    modalVm.$data.translations = getTranslations();
  });

  // Watch for translation updates from store
  const unwatchTranslations = store.watch(
    (state) => state.LocalizationModule?.translations,
    (translations) => {
      modalVm.$data.translations = translations;
    }
  );

  let warningTimer: number | null = null;
  let logoutTimer: number | null = null;
  let countdownInterval: number | null = null;

  function isTrackingEnabled() {
    return !!store.state.AccountModule?.isLogged;
  }

  function clearTimers() {
    if (warningTimer) {
      window.clearTimeout(warningTimer);
      warningTimer = null;
    }
    if (logoutTimer) {
      window.clearTimeout(logoutTimer);
      logoutTimer = null;
    }
    if (countdownInterval) {
      window.clearInterval(countdownInterval);
      countdownInterval = null;
    }
    modalVm.$data.show = false;
  }

  function showWarning() {
    const seconds = Math.ceil((LOGOUT_MS - WARNING_MS) / 1000);
    modalVm.$data.countdown = seconds;
    modalVm.$data.show = true;

    if (countdownInterval) {
      window.clearInterval(countdownInterval);
    }
    countdownInterval = window.setInterval(() => {
      modalVm.$data.countdown = Math.max(0, modalVm.$data.countdown - 1);
      if (modalVm.$data.countdown <= 0) {
        if (countdownInterval) {
          window.clearInterval(countdownInterval);
          countdownInterval = null;
        }
        forceLogout();
      }
    }, 1000);
  }

  function forceLogout(): void {
    store.dispatch(LOGOUT_USER as any).catch((error: any) => {
      // Error on logout - still log it but don't block (auth state already cleared)
      console.error('Failed to log out:', error);
    });
  }

  function resetTimers() {
    if (!isTrackingEnabled()) {
      clearTimers();
      return;
    }

    modalVm.$data.show = false;
    if (countdownInterval) {
      window.clearInterval(countdownInterval);
      countdownInterval = null;
    }
    if (warningTimer) {
      window.clearTimeout(warningTimer);
      warningTimer = null;
    }
    if (logoutTimer) {
      window.clearTimeout(logoutTimer);
      logoutTimer = null;
    }

    warningTimer = window.setTimeout(showWarning, WARNING_MS);
    logoutTimer = window.setTimeout(forceLogout, LOGOUT_MS);
  }

  function handleActivity() {
    if (!isTrackingEnabled()) {
      return;
    }

    // Once the warning is visible, require an explicit choice from the modal.
    if (modalVm.$data.show) {
      return;
    }

    resetTimers();
  }

  const activityEvents = [
    'mousemove',
    'keydown',
    'mousedown',
    'touchstart',
    'click',
    'scroll',
  ];

  activityEvents.forEach((ev) =>
    window.addEventListener(ev, handleActivity, { passive: true })
  );

  const unwatchAuth = store.watch(
    (state) => state.AccountModule.isLogged,
    (isLogged) => {
      if (isLogged) {
        resetTimers();
        return;
      }

      clearTimers();
    },
    { immediate: true }
  );

  const dispose = function dispose() {
    clearTimers();
    unwatchAuth();
    unwatchTranslations();
    activityEvents.forEach((ev) =>
      window.removeEventListener(ev, handleActivity as EventListener)
    );
    modalVm.$destroy();
    if (modalVm.$el && modalVm.$el.parentNode) {
      modalVm.$el.parentNode.removeChild(modalVm.$el);
    }
    try {
      // @ts-ignore
      delete (window as any).__sessionTimeoutVm__;
    } catch (e) {}
  };

  window.__sessionTimeoutDispose__ = dispose;

  return dispose;
}
